import { errorService } from '@/lib/errorService';
import type { AxiosError } from 'axios';

interface ApiErrorContext {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Processes an error caught from an API call, reports it,
 * and returns a user-friendly error message.
 *
 * @param caughtError The error object caught in a try...catch block.
 * @param operationContext Context about the API operation (e.g., 'searching contracts', 'fetching cancel reasons').
 * @param requestPayload The payload sent with the API request (for logging).
 * @returns A user-friendly error message string.
 */
export function handleApiError(
  caughtError: unknown,
  operationContext: string,
  requestPayload?: ApiErrorContext
): string {
  const error =
    caughtError instanceof Error ? caughtError : new Error(String(caughtError));

  // Extract status code and additional context from Axios error if available
  let statusCode: number | undefined;
  let endpoint: string | undefined;
  let method: string | undefined;
  let responseData: unknown;
  let isNetworkError = false;

  if (isAxiosError(caughtError)) {
    statusCode = caughtError.response?.status;
    endpoint = caughtError.config?.url;
    method = caughtError.config?.method?.toUpperCase();
    responseData = caughtError.response?.data;

    // Check for network errors using Axios error codes
    isNetworkError =
      caughtError.code === 'ERR_NETWORK' ||
      caughtError.code === 'NETWORK_ERROR' ||
      caughtError.message === 'Network Error';
  } else {
    // Fallback for non-Axios errors that might have statusCode property
    statusCode = (error as unknown as Record<string, unknown>).statusCode as
      | number
      | undefined;
  }

  // 1. Report detailed error with enhanced context
  errorService.reportApiError(error, operationContext, {
    endpoint,
    method,
    statusCode,
    isNetworkError,
    requestPayload,
    responseData: responseData as Record<string, unknown> | undefined,
  });

  // 2. Prepare user-friendly message
  let userFriendlyMessage = `An unexpected error occurred while ${operationContext}. Please try again. If the problem persists, please contact support.`;

  if (
    isNetworkError ||
    error.name === 'NetworkError' ||
    (typeof navigator !== 'undefined' && !navigator.onLine)
  ) {
    userFriendlyMessage = `Could not connect to the server while ${operationContext}. Please check your internet connection and try again.`;
  } else if (statusCode) {
    switch (statusCode) {
      case 400:
        userFriendlyMessage = `There was an issue with your request for ${operationContext}. Please check your inputs and try again.`;
        break;
      case 401: // Unauthorized
        userFriendlyMessage = `You are not authorized to perform this action (${operationContext}). Please try logging in again.`;
        break;
      case 403: // Forbidden
        userFriendlyMessage = `You do not have permission to ${operationContext}. Please contact support if you believe this is an error.`;
        break;
      case 404: // Not Found
        userFriendlyMessage = `The requested resource for ${operationContext} could not be found. Please check your details or contact support.`;
        break;
      case 500: // Internal Server Error
      case 502: // Bad Gateway
      case 503: // Service Unavailable
      case 504: // Gateway Timeout
        userFriendlyMessage = `The server encountered an issue while ${operationContext}. Please try again in a few moments.`;
        break;
      // Add more specific cases as needed
    }
  }
  // If it's a specific known error type from the backend (e.g., validation error with details),
  // you might further customize the message here based on error.message or other properties.

  return userFriendlyMessage;
}

/**
 * Type guard to check if an unknown error is an Axios error.
 *
 * Safely determines whether an error object is an AxiosError by checking
 * for the presence and value of the `isAxiosError` property. This allows
 * for safe access to Axios-specific error properties like response, config,
 * and request without type casting.
 *
 * @param error - Unknown error object to check
 *
 * @returns True if the error is an AxiosError, false otherwise
 *
 * @example
 * ```typescript
 * try {
 *   await apiClient.get('/api/data');
 * } catch (error) {
 *   if (isAxiosError(error)) {
 *     // TypeScript now knows error is AxiosError
 *     const statusCode = error.response?.status;
 *     const endpoint = error.config?.url;
 *     console.log(`API error ${statusCode} on ${endpoint}`);
 *   } else {
 *     // Handle non-Axios errors
 *     console.log('Non-Axios error:', error);
 *   }
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Usage in error handling
 * function handleError(error: unknown) {
 *   if (isAxiosError(error)) {
 *     // Access Axios-specific properties safely
 *     const { response, config, message } = error;
 *     return {
 *       status: response?.status,
 *       url: config?.url,
 *       message: message
 *     };
 *   }
 *   // Handle other error types
 *   return { message: String(error) };
 * }
 * ```
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}
